import { useGetAvlItemsQuery } from '@/services/approvedVendorList';
import { Box, FormikSelectInputNative, Button } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, AdderType, VendorEquipmentTypes } from 'types';

import {
  mapNonDcAvlManufacturerOptions,
  mapAvlModelNumberOptions,
  maybePrependCustomFreeTextOption,
} from '../SystemDesign/AvlFormUtils';

interface BatteryInputsProps {
  fieldName: string;
  isLoading: boolean;
  programType: ProgramType;
  index: number;
  readOnly?: boolean;
  orgAlias?: string;
}

export const BatteryInputs = ({
  fieldName,
  isLoading,
  programType,
  index,
  readOnly = false,
  orgAlias,
}: BatteryInputsProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { id: accountId } = useParams<{ id: string }>();
  // Server derives effectiveDate from accountId; never sent by the client.
  const { data: approvedVendorList, isLoading: isAppSettingsLoading } = useGetAvlItemsQuery(
    { equipmentType: VendorEquipmentTypes.storage, programType, orgAlias, accountId },
    { skip: !programType && !accountId },
  );
  const storage = approvedVendorList?.data ?? [];
  const filteredStorage = storage.filter((item) => {
    const isBackup = fieldName === AdderType.backupBattery;
    return isBackup ? item.backup : item.arbitrage;
  });
  const battery = values[fieldName]?.[index];
  const storageManufacturerOptions = useMemo(() => {
    if (isAppSettingsLoading) {
      return [];
    }
    const options = mapNonDcAvlManufacturerOptions(filteredStorage);
    return maybePrependCustomFreeTextOption(options, battery.manufacturer);
  }, [isAppSettingsLoading, filteredStorage, battery.manufacturer]);
  const storageModelOptions = useMemo(() => {
    if (isAppSettingsLoading) {
      return [];
    }
    const options = mapAvlModelNumberOptions(filteredStorage, battery.manufacturer);
    return maybePrependCustomFreeTextOption(options, battery.model);
  }, [isAppSettingsLoading, filteredStorage, battery.manufacturer, battery.model]);

  const batteryCountOptions = Array.from(Array(10)).map((_val, index) => ({ value: index + 1, label: index + 1 }));
  const shouldBeDisabled = isLoading || readOnly;
  const setManufacturer = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFieldValue(fieldName, [
        {
          type: fieldName,
          manufacturer: event.target.value,
          model: '',
          quantity: '',
        },
      ]);
    },
    [fieldName, setFieldValue],
  );

  return values?.[fieldName] ? (
    <Box
      direction={{ base: 'column', tablet: 'row' }}
      alignItems="center"
      childGap={{ base: 'md', tablet: 'md' }}
      width="100"
      style={{ margin: '0 0 20px 0' }}
    >
      <Field
        label="Manufacturer"
        name={`${fieldName}[${index}].manufacturer`}
        id={`${fieldName}[${index}].manufacturer`}
        options={storageManufacturerOptions}
        component={FormikSelectInputNative}
        isDisabled={shouldBeDisabled}
        onChange={setManufacturer}
        style={{
          visibility: index === 0 ? 'visible' : 'hidden',
        }}
      />
      {
        <Button
          variant="secondary"
          tone="neutral"
          onClick={() => {
            values[fieldName].splice(index, 1);
            setFieldValue(fieldName, values[fieldName]);
          }}
          iconPrefix="remove"
          size="sm"
          style={{
            padding: '10px',
            margin: '25px 15px 0 0',
            visibility: index === 0 ? 'hidden' : 'visible',
          }}
          isDisabled={shouldBeDisabled}
        />
      }
      <Field
        label="Model"
        name={`${fieldName}[${index}].model`}
        id={`${fieldName}[${index}].model`}
        options={storageModelOptions}
        component={FormikSelectInputNative}
        isDisabled={shouldBeDisabled}
        style={{}}
      />
      <Field
        label="Quantity"
        name={`${fieldName}[${index}].quantity`}
        id={`${fieldName}[${index}].quantity`}
        options={batteryCountOptions}
        component={FormikSelectInputNative}
        isDisabled={shouldBeDisabled}
        style={{
          width: '225px',
        }}
      />
    </Box>
  ) : null;
};
